/**
 * Collectible Madness — custom.js
 * Free-range speed helpers. Does not change visual design.
 *
 * Theme hooks still available:
 *   document.addEventListener('variant:changed', ...)
 *   document.addEventListener('product:added', ...)
 *   document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', ...))
 */
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * Ensure product / gallery images decode async if Liquid missed the attribute.
   */
  function ensureAsyncDecoding() {
    var imgs = document.querySelectorAll(
      '.product-item img, .product-gallery__image, .collection__image-wrapper img, .cm-pr__main-image'
    );
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].getAttribute('decoding')) {
        imgs[i].decoding = 'async';
      }
    }
  }

  /**
   * Desktop: warm the product URL on intentional hover (short delay), so click
   * feels instant — without Instant.page viewport prefetch thrash on whole grids.
   * Only runs when hover is real (fine pointer).
   */
  function setupHoverWarm() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }
    if (!window.fetch) return;

    var timer = null;
    var lastHref = '';

    document.addEventListener(
      'mouseover',
      function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a.product-item__image-wrapper, a.product-item__title') : null;
        if (!a || !a.href) return;
        if (a.dataset.cmWarmed === '1') return;
        var href = a.href;
        if (href === lastHref) return;
        lastHref = href;
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          a.dataset.cmWarmed = '1';
          try {
            // low-priority warm; ignore errors (CORS/cache still helps most same-origin navigations)
            fetch(href, { method: 'GET', credentials: 'same-origin', priority: 'low' }).catch(function () {});
          } catch (err) {}
        }, 120);
      },
      { passive: true }
    );

    document.addEventListener(
      'mouseout',
      function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a.product-item__image-wrapper, a.product-item__title') : null;
        if (!a) return;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        lastHref = '';
      },
      { passive: true }
    );
  }

  /**
   * Desktop hover: inject the second product photo on first hover only.
   * Keeps hover swap, but the first HTML / mobile download skips those images.
   */
  function setupSecondaryOnHover() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }

    function injectSecondary(wrap) {
      if (!wrap || wrap.getAttribute('data-cm-secondary-loaded') === '1') return;
      var src = wrap.getAttribute('data-cm-secondary-src');
      if (!src) return;
      var box = wrap.querySelector('.aspect-ratio');
      if (!box) return;
      var img = document.createElement('img');
      img.className = 'product-item__secondary-image';
      img.src = src;
      var srcset = wrap.getAttribute('data-cm-secondary-srcset');
      var sizes = wrap.getAttribute('data-cm-secondary-sizes');
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      img.alt = '';
      img.decoding = 'async';
      img.setAttribute('aria-hidden', 'true');
      box.appendChild(img);
      wrap.setAttribute('data-cm-secondary-loaded', '1');
    }

    document.addEventListener(
      'mouseover',
      function (e) {
        var wrap = e.target && e.target.closest ? e.target.closest('[data-cm-secondary]') : null;
        if (wrap) injectSecondary(wrap);
      },
      { passive: true }
    );
  }

  /**
   * Desktop-only extra cards: attach real image src after first paint
   * so phones never download those photos.
   */
  function hydrateDeferredCardImages() {
    if (!window.matchMedia || !window.matchMedia('(min-width: 750px)').matches) {
      return;
    }
    var imgs = document.querySelectorAll('img[data-cm-defer-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.getAttribute('src')) continue;
      img.src = img.getAttribute('data-cm-defer-src');
      var srcset = img.getAttribute('data-cm-defer-srcset');
      var sizes = img.getAttribute('data-cm-defer-sizes');
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
    }
  }

  /**
   * Keep desktop collection filters parked under the sticky header
   * while the product column is in view.
   */
  function setupStickyFilters() {
    if (!document.querySelector('.layout__section--secondary')) return;

    function setOffset() {
      var header =
        document.querySelector('#shopify-section-group-header-group') ||
        document.querySelector('.shopify-section-group-header-group') ||
        document.querySelector('#shopify-section-header');
      var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--cm-sticky-offset', h + 12 + 'px');
    }

    setOffset();
    window.addEventListener('resize', setOffset);
    if (window.ResizeObserver && document.querySelector('#shopify-section-group-header-group, .shopify-section-group-header-group')) {
      var ro = new ResizeObserver(setOffset);
      var group = document.querySelector('#shopify-section-group-header-group, .shopify-section-group-header-group');
      if (group) ro.observe(group);
    }
  }

  /**
   * Collection pages: Hide / Show the desktop filter sidebar (Gameology-style).
   */
  function setupFilterToggle() {
    var layout = document.querySelector('.cm-collection-layout');
    var btn = document.querySelector('[data-cm-toggle-filters]');
    if (!layout || !btn) return;

    var key = 'cmHideCollectionFilters';
    function apply(hidden) {
      layout.classList.toggle('is-filters-hidden', hidden);
      btn.setAttribute('aria-pressed', hidden ? 'true' : 'false');
      btn.classList.toggle('is-filters-hidden', hidden);
    }

    try {
      apply(window.localStorage.getItem(key) === '1');
    } catch (e) {
      apply(false);
    }

    btn.addEventListener('click', function () {
      var hidden = !layout.classList.contains('is-filters-hidden');
      apply(hidden);
      try {
        window.localStorage.setItem(key, hidden ? '1' : '0');
      } catch (err) {}
    });
  }

  /**
   * PDP BNPL: logo + ? opens the official Afterpay / Zip / Klarna popup.
   * Widgets are visually hidden; we click them at tap-time so async apps
   * (Klarna OSM, Afterpay snippet, Zip iframe) can finish loading first.
   */
  function setupBnplLogos() {
    var root = document.querySelector('[data-cm-bnpl]');
    if (!root) return;

    function clickFirst(node, selector) {
      if (!node || !node.querySelector) return false;
      var el = node.querySelector(selector);
      if (!el) return false;
      el.click();
      return true;
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents);
      }
      return '$' + (Math.round(cents) / 100).toFixed(2);
    }

    function updateAfterpayAmount(cents) {
      var btn = root.querySelector('[data-cm-bnpl-open="afterpay"]');
      var amt = root.querySelector('[data-cm-afterpay-amt]');
      if (!btn) return;
      var min = parseInt(btn.getAttribute('data-cm-afterpay-min') || '4', 10);
      var max = parseInt(btn.getAttribute('data-cm-afterpay-max') || '100000', 10);
      var ok = cents >= min && cents <= max;
      btn.hidden = !ok;
      if (!ok || !amt) return;
      var instalment = formatMoney(cents / 4);
      amt.textContent = '4 payments of ' + instalment;
      btn.setAttribute('aria-label', 'Afterpay — 4 payments of ' + instalment + '. Learn more');
    }

    function clickShadowLearnMore(host) {
      if (!host) return false;
      var roots = [host];
      if (host.shadowRoot) roots.push(host.shadowRoot);
      for (var r = 0; r < roots.length; r++) {
        var scope = roots[r];
        if (!scope || !scope.querySelectorAll) continue;
        var nodes = scope.querySelectorAll('button, a, [role="button"], [aria-label], [part]');
        for (var i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          var label = (
            (el.getAttribute('aria-label') || '') +
            ' ' +
            (el.textContent || '') +
            ' ' +
            (el.getAttribute('part') || '')
          ).toLowerCase();
          if (/learn|info|more|modal|afterpay/.test(label) || el.tagName === 'BUTTON' || el.tagName === 'A') {
            el.click();
            return true;
          }
        }
        var nested = scope.querySelectorAll('*');
        for (var j = 0; j < nested.length; j++) {
          if (nested[j].shadowRoot && clickShadowLearnMore(nested[j])) return true;
        }
      }
      return false;
    }

    function openAfterpay(btn) {
      // Official Afterpay hook: data-afterpay-modal on the chip. Do not send
      // shoppers to afterpay.com — Square opens the 4-payments info modal.
      if (btn && btn.hasAttribute('data-afterpay-modal')) return;
      var place = document.querySelector('square-placement[data-page-type="product"], square-placement');
      if (place && clickShadowLearnMore(place)) return;
    }

    function openZip() {
      var el = document.getElementById('zip-prod-widget');
      if (el) {
        var inner = el.querySelector('a, button, [role="button"], iframe');
        if (inner && inner.tagName !== 'IFRAME') {
          inner.click();
          return;
        }
        el.click();
        return;
      }
      window.open('https://zip.co/au', '_blank', 'noopener');
    }

    function openKlarna() {
      var place = document.querySelector('klarna-placement');
      if (!place) {
        window.open('https://www.klarna.com/au/', '_blank', 'noopener');
        return;
      }
      var learnSel = 'button, a, [role="button"], [class*="learn"], [aria-label*="learn" i]';
      if (place.shadowRoot && clickFirst(place.shadowRoot, learnSel)) return;
      if (clickFirst(place, learnSel)) return;
      place.click();
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cm-bnpl-open]');
      if (!btn) return;
      var which = btn.getAttribute('data-cm-bnpl-open');
      if (which === 'afterpay') {
        // Let Square handle data-afterpay-modal on this same click.
        openAfterpay(btn);
        return;
      }
      e.preventDefault();
      if (which === 'zip') openZip();
      if (which === 'klarna') openKlarna();
    });

    document.addEventListener('variant:changed', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;
      updateAfterpayAmount(variant.price);
    });
  }

  /**
   * Chat + CM Rewards: homepage only. Hide both in the mini-cart.
   * On home, hide Rewards while Chat is open so it cannot sit on the composer.
   */
  function setupLauncherHomeOnly() {
    var CHAT_SEL =
      'shopify-chat, #shopify-chat, #ShopifyChat, #dummy-chat-button-iframe, ' +
      '#tidio-chat, iframe#tidio-chat-button, #gorgias-chat-container';
    var REWARD_SEL =
      '#smile-ui-container, #smile-ui-lite-container, .cm-rewards-float';

    function labelOf(el) {
      return (
        (el.getAttribute('title') || '') +
        ' ' +
        (el.id || '') +
        ' ' +
        (el.getAttribute('name') || '') +
        ' ' +
        (el.className || '')
      ).toLowerCase();
    }

    function isChatFrame(el) {
      var t = labelOf(el);
      return /chat|tidio|gorgias|inbox/.test(t) && !/smile|reward/.test(t);
    }

    function isRewardFrame(el) {
      return /smile|reward/.test(labelOf(el));
    }

    function collect(sel, test) {
      var list = [];
      document.querySelectorAll(sel).forEach(function (el) {
        list.push(el);
      });
      document.querySelectorAll('iframe').forEach(function (el) {
        if (test(el) && list.indexOf(el) === -1) list.push(el);
      });
      return list;
    }

    function setHidden(el, hide) {
      if (!el || !el.style) return;
      if (hide) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      } else {
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
      }
    }

    function cartIsOpen() {
      var cart = document.getElementById('mini-cart');
      return !!(cart && cart.getAttribute('aria-hidden') === 'false');
    }

    function chatIsOpen() {
      var frames = document.querySelectorAll('iframe');
      for (var i = 0; i < frames.length; i++) {
        var t = (frames[i].getAttribute('title') || '').toLowerCase();
        if (t.indexOf('chat') !== -1 && t.indexOf('button') === -1) {
          if (frames[i].getBoundingClientRect().height > 160) return true;
        }
      }
      var sc = document.querySelector('shopify-chat');
      if (sc && (sc.hasAttribute('open') || sc.getAttribute('aria-expanded') === 'true')) {
        return true;
      }
      return false;
    }

    function onHome() {
      return document.body.classList.contains('template-index');
    }

    function sync() {
      var hideChat = !onHome() || cartIsOpen();
      var hideRewards = hideChat || chatIsOpen();
      document.documentElement.classList.toggle('cm-launchers-off', hideChat);
      document.documentElement.classList.toggle('cm-rewards-off', hideRewards);
      collect(CHAT_SEL, isChatFrame).forEach(function (el) {
        setHidden(el, hideChat);
      });
      collect(REWARD_SEL, isRewardFrame).forEach(function (el) {
        setHidden(el, hideRewards);
      });
    }

    sync();
    setTimeout(sync, 600);
    setTimeout(sync, 1800);
    setTimeout(sync, 4000);
    setInterval(sync, 1200);

    var cart = document.getElementById('mini-cart');
    if (cart && window.MutationObserver) {
      new MutationObserver(sync).observe(cart, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class']
      });
    }
    document.addEventListener('click', function () {
      setTimeout(sync, 80);
    });
  }

  function refreshProductGallery() {
    if (!document.body.classList.contains('template-product')) return;
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Park Filter / Sort on the visible bottom of the phone, just above
   * Safari’s toolbar (visualViewport), not in the middle of the products.
   */
  function pinFilterToolbar() {
    var collection =
      document.body.classList.contains('template-collection') ||
      document.body.classList.contains('template-search');
    if (!collection) return;
    if (window.matchMedia && !window.matchMedia('(max-width: 999px)').matches) {
      document.documentElement.style.removeProperty('--cm-filter-bar-bottom');
      return;
    }

    var gap = 10;
    var bottom = gap;
    if (window.visualViewport) {
      var vv = window.visualViewport;
      bottom = Math.max(gap, window.innerHeight - (vv.offsetTop + vv.height) + gap);
    }
    document.documentElement.style.setProperty('--cm-filter-bar-bottom', bottom + 'px');
  }

  onReady(function () {
    ensureAsyncDecoding();
    setupHoverWarm();
    setupSecondaryOnHover();
    setupFilterToggle();
    setupStickyFilters();
    setupBnplLogos();
    setupLauncherHomeOnly();
    pinFilterToolbar();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', pinFilterToolbar);
      window.visualViewport.addEventListener('scroll', pinFilterToolbar);
    }
    window.addEventListener('resize', pinFilterToolbar);
    setTimeout(refreshProductGallery, 250);
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        hydrateDeferredCardImages();
      });
    } else {
      hydrateDeferredCardImages();
    }
  });
})();